import React, { useRef, useEffect, useCallback } from "react";

const EDITOR_ID = "simple-rte-editor";

const SimpleRichTextEditor = ({ value, onChange, placeholder = "Write something..." }) => {
    const editorRef = useRef(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            if (isFirstRender.current || document.activeElement !== editorRef.current) {
                editorRef.current.innerHTML = value || "";
                isFirstRender.current = false;
            }
        }
    }, [value]);

    const handleInput = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const exec = useCallback((command, arg = null) => {
        document.execCommand(command, false, arg);
        editorRef.current?.focus();
        handleInput();
    }, []);

    const applyHeading = (tag) => {
        exec('formatBlock', tag);
    };

    const applyFontSize = (size) => {
        exec('fontSize', '7');
        if (editorRef.current) {
            const fonts = editorRef.current.querySelectorAll('font[size="7"]');
            fonts.forEach(f => {
                const span = document.createElement('span');
                span.style.fontSize = size;
                span.innerHTML = f.innerHTML;
                f.replaceWith(span);
            });
            handleInput();
        }
    };

    const insertLink = () => {
        const url = window.prompt("Enter URL:");
        if (url) exec('createLink', url);
    };

    const btnStyle = {
        border: "1px solid #ced4da",
        background: "#fff",
        borderRadius: "4px",
        padding: "4px 8px",
        fontSize: "13px",
        cursor: "pointer",
        lineHeight: 1,
    };

    return (
        <div style={{ border: "1px solid #ced4da", borderRadius: "4px", overflow: "hidden" }}>
            {/* ---- Word-jaisi styling editor ke andar ke content ke liye ---- */}
            <style>{`
                #${EDITOR_ID} {
                    font-family: "Aptos", "Aptos Display", "Segoe UI", "Nirmala UI", Arial, sans-serif;
                    line-height: 1.6;
                    color: #726e6e;
                }
                #${EDITOR_ID} h2 {
                    font-size: 20px;
                    font-weight: 700;
                    margin: 18px 0 10px;
                }
                #${EDITOR_ID} h3 {
                    font-size: 17px;
                    font-weight: 700;
                    margin: 16px 0 8px;
                }
                #${EDITOR_ID} h4 {
                    font-size: 15px;
                    font-weight: 700;
                    margin: 14px 0 6px;
                }
                #${EDITOR_ID} p {
                    margin: 0 0 12px;
                    font-size: 14px;
                }
                #${EDITOR_ID} ul,
                #${EDITOR_ID} ol {
                    margin: 0 0 14px;
                    padding-left: 24px;
                }
                #${EDITOR_ID} ul li,
                #${EDITOR_ID} ol li {
                    margin-bottom: 8px;
                    font-size: 14px;
                }
                #${EDITOR_ID} a {
                    color: #2563eb;
                    text-decoration: underline;
                }
                #${EDITOR_ID}:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                }
            `}</style>

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    padding: "6px",
                    background: "#f8f9fa",
                    borderBottom: "1px solid #ced4da",
                }}
            >
                <select
                    style={btnStyle}
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) applyHeading(e.target.value); e.target.value = ""; }}
                >
                    <option value="">Heading</option>
                    <option value="H2">Heading 2</option>
                    <option value="H3">Heading 3</option>
                    <option value="H4">Heading 4</option>
                    <option value="P">Normal</option>
                </select>

                {/* Font size dropdown - shows the actual point/px number instead of Small/Normal/Large labels */}
                <select
                    style={btnStyle}
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) applyFontSize(e.target.value); e.target.value = ""; }}
                >
                    <option value="">Size</option>
                    <option value="8px">8</option>
                    <option value="10px">10</option>
                    <option value="12px">12</option>
                    <option value="14px">14</option>
                    <option value="16px">16</option>
                    <option value="18px">18</option>
                    <option value="20px">20</option>
                    <option value="22px">22</option>
                    <option value="24px">24</option>
                    <option value="28px">28</option>
                    <option value="32px">32</option>
                    <option value="36px">36</option>
                    <option value="48px">48</option>
                </select>

                <button type="button" style={{ ...btnStyle, fontWeight: "bold" }} onClick={() => exec('bold')}>B</button>
                <button type="button" style={{ ...btnStyle, fontStyle: "italic" }} onClick={() => exec('italic')}>I</button>
                <button type="button" style={{ ...btnStyle, textDecoration: "underline" }} onClick={() => exec('underline')}>U</button>
                <button type="button" style={{ ...btnStyle, textDecoration: "line-through" }} onClick={() => exec('strikeThrough')}>S</button>

                <input
                    type="color"
                    title="Text color"
                    style={{ width: "32px", height: "30px", padding: "0", border: "1px solid #ced4da", borderRadius: "4px" }}
                    onChange={(e) => exec('foreColor', e.target.value)}
                />
                <input
                    type="color"
                    title="Highlight color"
                    style={{ width: "32px", height: "30px", padding: "0", border: "1px solid #ced4da", borderRadius: "4px" }}
                    onChange={(e) => exec('hiliteColor', e.target.value)}
                />

                <button type="button" style={btnStyle} onClick={() => exec('insertOrderedList')}>1. List</button>
                <button type="button" style={btnStyle} onClick={() => exec('insertUnorderedList')}>• List</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyLeft')}>⯇</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyCenter')}>☰</button>
                <button type="button" style={btnStyle} onClick={() => exec('justifyRight')}>⯈</button>
                <button type="button" style={btnStyle} onClick={insertLink}>Link</button>
                <button type="button" style={btnStyle} onClick={() => exec('removeFormat')}>Clear</button>
            </div>

            {/* ---------------- EDITOR AREA ---------------- */}
            <div
                id={EDITOR_ID}
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                data-placeholder={placeholder}
                style={{
                    minHeight: "150px",
                    padding: "10px 12px",
                    outline: "none",
                    background: "#fff",
                    fontSize: "14px",
                }}
                suppressContentEditableWarning
            />
        </div>
    );
};

export default SimpleRichTextEditor;